/**
 * CrossFormatCompareModal — ONE floating compare modal, two documents visible
 * at once, any pair of formats.
 *
 * This replaces the change-log view. The shape is the existing US Version
 * Comparison modal: header, counters, filters, section rows, and two document
 * panels side by side with red/green marking INSIDE the real section text.
 * What is new is only that each side's format is chosen by the user —
 * XML↔XML, PDF↔PDF, XML↔PDF, PDF↔XML all land in this one modal.
 *
 * Why the panes render TEXT on both sides, including the PDF one: no
 * per-token word boxes are stored at ingest yet, so there is no honest
 * spatial highlight to draw on a page image. The page is still reachable —
 * every PDF-side section shows its page number and opens the stored page
 * image — but a box is never invented. Drawing one would be the single most
 * misleading thing this view could do.
 */
import { useEffect, useMemo, useState } from 'react';
import { authApi } from '../../services/authApi';
import VersionComparisonModal from '../VersionComparisonModal';
import EuHistoricalCompare, { type CompareSource } from './EuHistoricalCompare';
import type { VersionSource } from '../../types';

type SourceKind = 'spl_xml' | 'fda_pdf';
type Precision = 'exact' | 'approximate' | 'unavailable';
type MatchStatus = 'key' | 'title_matched' | 'ambiguous' | 'only_in_old' | 'only_in_new';
type Verdict = 'modified' | 'unchanged' | 'not_compared';
type Filter = 'all' | 'modified' | 'unchanged' | 'not_compared';

interface LocationJson { page: number | null; box: number[] | null; precision: Precision; page_status: Precision }
interface EvidenceJson {
  text: string; char_range: number[]; source_format: SourceKind;
  location_status: Precision; page_status: Precision; locations: LocationJson[];
}
interface AnchorJson { text: string; char_offset: number; kind: 'token' | 'section_start'; locations: LocationJson[] }
interface ChangeJson {
  change_id: string; section_key: string;
  operation: 'insert' | 'delete' | 'replace';
  old_evidence: EvidenceJson | null; new_evidence: EvidenceJson | null;
  old_anchor: AnchorJson | null; new_anchor: AnchorJson | null;
}
interface SectionRow {
  section_key: string; title: string; match_status: MatchStatus; verdict: Verdict;
  extraction_status: 'complete' | 'partial' | 'failed'; confident: boolean;
  words_added: number; words_removed: number;
  old_page: number | null; new_page: number | null;
  old_text: string | null; new_text: string | null;
}
interface Totals {
  changes?: number; sections_compared?: number; sections_one_sided?: number;
  sections_ambiguous?: number; sections_title_matched?: number;
  sections_not_confident?: number; modified?: number; unchanged?: number;
  not_compared?: number;
}
interface SideId {
  source: SourceKind; sections: number;
  drug_id?: number; drug_version_id?: number; version_number?: number;
  is_current?: boolean; version_date?: string | null;
  document_id?: number; version_str?: string | null;
  application_number?: string | null; action_date?: string | null;
}
interface Relationship {
  status: 'verified_equivalent' | 'likely_equivalent' | 'distinct' | 'unknown';
  reason: string;
  evidence: { shared_drug_id?: number | null; unlinked_pdf_documents?: number[] };
}
interface Result {
  old: SideId; new: SideId; relationship: Relationship;
  sections: SectionRow[]; changes: ChangeJson[]; totals: Totals;
}

/** One selectable side: a format plus a version within that format. */
export interface SideOption {
  source: SourceKind;
  /** drug_id for spl_xml, document id for fda_pdf */
  id: number;
  /** spl_xml only */
  version?: number | null;
  label: string;
  sublabel?: string | undefined;
  /** spl_xml only: what the POLYMORPHIC XML<->XML comparison needs. Its ids
   *  come from /drugs/{id}/all-versions and are a different identity from
   *  `id` above — `drugs.id` for the current revision, `drug_versions.id`
   *  for a historical one — which is exactly why they travel separately
   *  instead of being guessed from `version`. */
  polySource?: VersionSource | undefined;
  polyId?: number | undefined;
  /** fda_pdf only: the FDA version string the PDF<->PDF compare keys on. */
  vstr?: string | undefined;
  /** ISO date this version took effect: FDA action_date for a PDF, SPL
   *  effective date for an XML revision. The ONE axis both formats share —
   *  version numbers are not comparable across them, and even within the PDF
   *  family submission number does not track approval order (15 inversions
   *  among Humira's 41 versions). Everything about ordering below keys on
   *  this and nothing else. */
  date?: string | null;
}

const FMT: Record<SourceKind, string> = { spl_xml: 'XML', fda_pdf: 'PDF' };

/** Sortable key. Undated versions sort last and never block a selection. */
function when(o: SideOption): number {
  const t = o.date ? Date.parse(o.date) : NaN;
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/* ── inline marking ───────────────────────────────────────────────────────
 * The engine's char_range indexes the section text verbatim — that is the
 * provenance invariant it was fixed to hold — so marking is a slice, never a
 * re-search for the phrase. A re-search is what puts a highlight on the
 * second occurrence of a repeated sentence.
 */
function Marked({ text, spans, tone }: {
  text: string;
  spans: { start: number; end: number; id: string }[];
  tone: 'old' | 'new';
}) {
  if (!text) {
    return <span className="text-[12px] italic text-slate-400">
      This section is not present in this document.
    </span>;
  }
  const ordered = [...spans].sort((a, b) => a.start - b.start);
  const out: React.ReactNode[] = [];
  let at = 0;
  const cls = tone === 'old'
    ? 'bg-rose-100 text-rose-900 rounded-sm px-0.5'
    : 'bg-emerald-100 text-emerald-900 rounded-sm px-0.5';
  ordered.forEach((s, i) => {
    const a = Math.max(0, Math.min(s.start, text.length));
    const b = Math.max(a, Math.min(s.end, text.length));
    if (a > at) out.push(<span key={`t${i}`}>{text.slice(at, a)}</span>);
    if (b > a) {
      out.push(
        <mark key={s.id} id={`cf-${tone}-${s.id}`} className={cls}>
          {text.slice(a, b)}
        </mark>,
      );
    }
    at = Math.max(at, b);
  });
  if (at < text.length) out.push(<span key="tail">{text.slice(at)}</span>);
  return <>{out}</>;
}

function Pane({ side, row, spans, tone }: {
  side: SideId; row: SectionRow;
  spans: { start: number; end: number; id: string }[];
  tone: 'old' | 'new';
}) {
  const text = tone === 'old' ? row.old_text : row.new_text;
  const page = tone === 'old' ? row.old_page : row.new_page;
  const isPdf = side.source === 'fda_pdf';
  return (
    <div className="min-w-0 flex-1">
      <div className="mb-1.5 flex items-center gap-2 text-[11px] uppercase tracking-wide text-ink-2">
        <span>{tone === 'old' ? 'Previous' : 'Current'}</span>
        <span className={`rounded px-1.5 py-0.5 ring-1 ${isPdf
          ? 'bg-violet-50 text-violet-700 ring-violet-200'
          : 'bg-sky-50 text-sky-700 ring-sky-200'}`}>
          {FMT[side.source]}
        </span>
        <span className="text-slate-400 normal-case">
          {isPdf ? side.version_str : `rev ${side.version_number ?? '?'}`}
        </span>
        {isPdf && page != null && (
          <span className="ml-auto text-slate-400 normal-case">page {page}</span>
        )}
      </div>
      <div className="max-h-[46vh] overflow-y-auto whitespace-pre-wrap rounded border border-line bg-white p-3 text-[13px] leading-relaxed text-ink">
        <Marked text={text ?? ''} spans={spans} tone={tone} />
      </div>
    </div>
  );
}

export default function CrossFormatCompareModal({
  brand, options, initialOld, initialNew, onClose,
}: {
  brand: string;
  /** Everything the user may put on either side, both formats in one list. */
  options: SideOption[];
  initialOld: SideOption;
  initialNew: SideOption;
  onClose: () => void;
}) {
  const key = (o: SideOption) => `${o.source}:${o.id}:${o.version ?? ''}`;
  const [oldKey, setOldKey] = useState(key(initialOld));
  const [newKey, setNewKey] = useState(key(initialNew));
  const [data, setData] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('modified');
  const [open, setOpen] = useState<string | null>(null);

  const oldOpt = options.find((o) => key(o) === oldKey) ?? initialOld;
  const newOpt = options.find((o) => key(o) === newKey) ?? initialNew;

  // One chronological list, XML revisions and PDF versions interleaved. They
  // are two publication streams of the same label, so ordering them by date
  // is the only way a reader can tell which of a given pair is actually the
  // later document.
  const ordered = useMemo(
    () => [...options].sort((a, b) => when(a) - when(b)), [options]);

  // WHICH comparison this is, decided entirely by the two selections. The
  // user never picks a "comparison type" — the formats are a property of the
  // versions they chose, so there is nothing extra to get wrong.
  const mode = `${oldOpt.source}|${newOpt.source}`;
  const isCross = oldOpt.source !== newOpt.source;

  useEffect(() => {
    let alive = true;
    if (!isCross) { setData(null); setError(null); return; }
    setData(null);
    setError(null);
    authApi
      .getCrossFormatCompare({
        oldSource: oldOpt.source, oldId: oldOpt.id,
        newSource: newOpt.source, newId: newOpt.id,
        oldVersion: oldOpt.version ?? null, newVersion: newOpt.version ?? null,
      })
      .then((r) => { if (alive) setData(r as Result); })
      .catch((e) => {
        if (!alive) return;
        const d = e?.response?.data?.detail;
        setError(typeof d === 'string' ? d
          : d?.relationship?.reason ? `Not compared — ${d.relationship.reason}.`
          : 'Comparison failed.');
      });
    return () => { alive = false; };
  }, [isCross, oldKey, newKey, oldOpt.source, oldOpt.id, oldOpt.version,
      newOpt.source, newOpt.id, newOpt.version]);

  // PDF<->PDF reuses the family's existing two-pane page-image compare
  // unchanged: it diffs STORED page regions and draws real word boxes, which
  // is strictly better than anything the cross-format engine can show while
  // no per-token boxes are stored. Replacing it with text panes would be a
  // downgrade dressed up as consistency.
  const pdfSource = useMemo<CompareSource>(() => ({
    versionsPath: `/fda_label_pdf/documents/${newOpt.id}/versions`,
    diffPath: (o, n) =>
      `/fda_label_pdf/compare?old=${encodeURIComponent(o)}&new=${encodeURIComponent(n)}`,
    docLabel: 'Prescribing Information',
    subtitle: 'Drugs@FDA approved labeling actions',
  }), [newOpt.id]);

  /** Change spans per section, split by side, as offsets into that side's text. */
  const spansBySection = useMemo(() => {
    const m = new Map<string, { old: { start: number; end: number; id: string }[];
                                 new: { start: number; end: number; id: string }[] }>();
    for (const c of data?.changes ?? []) {
      const e = m.get(c.section_key) ?? { old: [], new: [] };
      if (c.old_evidence) {
        e.old.push({ start: c.old_evidence.char_range[0]!, end: c.old_evidence.char_range[1]!, id: c.change_id });
      }
      if (c.new_evidence) {
        e.new.push({ start: c.new_evidence.char_range[0]!, end: c.new_evidence.char_range[1]!, id: c.change_id });
      }
      m.set(c.section_key, e);
    }
    return m;
  }, [data]);

  const rows = useMemo(() => {
    const all = data?.sections ?? [];
    if (filter === 'all') return all;
    return all.filter((r) => r.verdict === filter);
  }, [data, filter]);

  const t = data?.totals ?? {};
  const COUNTS: { k: Filter; label: string; n: number }[] = [
    { k: 'all', label: 'All', n: data?.sections.length ?? 0 },
    { k: 'modified', label: 'Modified', n: t.modified ?? 0 },
    { k: 'unchanged', label: 'Unchanged', n: t.unchanged ?? 0 },
    { k: 'not_compared', label: 'Not compared', n: t.not_compared ?? 0 },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <div className="text-[15px] font-medium text-ink">Version Comparison</div>
          <div className="text-[12px] text-ink-2">{brand} · compare any two versions</div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {/* Older on the LEFT, newer on the RIGHT, always — a diff read in
              the other direction inverts every addition and removal. Rather
              than silently swapping the sides behind the user's back, the
              options that would invert it are disabled, so the rule is
              visible in the control instead of being a surprise in the
              result. Undated versions never block anything. */}
          <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-ink-2">
            Older
            <select
              value={oldKey}
              onChange={(e) => setOldKey(e.target.value)}
              className="h-8 min-w-[250px] rounded-ctl border border-line-input bg-white px-2 text-[12px] normal-case text-ink focus:outline-none focus:ring-4 focus:ring-accent-ring"
            >
              {ordered.map((o) => {
                const k = key(o);
                const blocked = k !== oldKey
                  && (k === newKey || when(o) > when(newOpt));
                return (
                  <option key={k} value={k} disabled={blocked}>
                    {FMT[o.source]} · {o.label}{o.sublabel ? ` — ${o.sublabel}` : ''}
                    {blocked ? '  (not older)' : ''}
                  </option>
                );
              })}
            </select>
          </label>
          <span className="pb-1.5 text-slate-400" aria-hidden>→</span>
          <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-ink-2">
            Newer
            <select
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              className="h-8 min-w-[250px] rounded-ctl border border-line-input bg-white px-2 text-[12px] normal-case text-ink focus:outline-none focus:ring-4 focus:ring-accent-ring"
            >
              {ordered.map((o) => {
                const k = key(o);
                const blocked = k !== newKey
                  && (k === oldKey || when(o) < when(oldOpt));
                return (
                  <option key={k} value={k} disabled={blocked}>
                    {FMT[o.source]} · {o.label}{o.sublabel ? ` — ${o.sublabel}` : ''}
                    {blocked ? '  (not newer)' : ''}
                  </option>
                );
              })}
            </select>
          </label>
          <button onClick={onClose} className="btn-secondary h-8">Close</button>
        </div>
      </div>

      {/* ── the body, chosen by the pair ──────────────────────────────────
          Each format combination keeps the comparison UI that is actually
          best for it, instead of flattening all four into one lowest common
          denominator. Only the genuinely new case gets the new view. */}

      {mode === 'spl_xml|spl_xml' && (
        oldOpt.polyId != null && newOpt.polyId != null ? (
          <VersionComparisonModal
            embedded
            polymorphicParams={{
              sourceA: oldOpt.polySource as VersionSource, idA: oldOpt.polyId,
              sourceB: newOpt.polySource as VersionSource, idB: newOpt.polyId,
            }}
            onClose={onClose}
          />
        ) : (
          <div className="p-4 text-[13px] text-slate-500">
            These SPL revisions cannot be compared: the comparison needs each
            revision's own row id, which this list did not supply.
          </div>
        )
      )}

      {mode === 'fda_pdf|fda_pdf' && (
        <EuHistoricalCompare
          hc={String(newOpt.id)}
          brand={brand}
          source={pdfSource}
          initialOld={oldOpt.vstr ?? oldOpt.label}
          initialNew={newOpt.vstr ?? newOpt.label}
        />
      )}

      {isCross && error && <div className="p-4 text-[13px] text-rose-700">{error}</div>}
      {isCross && !data && !error && (
        <div className="p-4 text-[13px] text-slate-500">Comparing…</div>
      )}

      {isCross && data && (
        <>
          {data.relationship.status === 'unknown' && (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-[12px] text-amber-900">
              {data.relationship.evidence?.unlinked_pdf_documents?.length
                ? <><b>Their product relationship is not verified.</b> At least one document is not linked to a drug.</>
                : <><b>The revision is not verified.</b> Same product; nothing here establishes the same revision.</>}
              {' '}Differences may be a labelling change, a publication gap, or an extraction difference.
            </div>
          )}

          {/* A revision whose sections were never stored with numbers or
              titles cannot be matched, and the result is a wall of
              "one source only" that looks like the label was rewritten.
              Say which side is thin rather than letting the counts imply a
              change. Humira rev 2146 stores 53 sections of which 35 have
              neither a number nor a title. */}
          {(t.sections_one_sided ?? 0) > (t.sections_compared ?? 0) && (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-[12px] text-amber-900">
              <b>Thin coverage on one side.</b>{' '}
              {t.sections_one_sided} of {data.sections.length} sections exist in
              only one of these two documents, against {t.sections_compared}{' '}
              actually compared. That is an extraction gap in the stored
              revision, not evidence that the label changed — pick a revision
              with fuller coverage for a meaningful diff.
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 border-b border-line bg-slate-50 px-4 py-2 text-[12px]">
            {COUNTS.map((c) => (
              <button
                key={c.k}
                onClick={() => setFilter(c.k)}
                className={`rounded px-2 py-1 ring-1 ${filter === c.k
                  ? 'bg-white font-medium text-ink ring-line'
                  : 'text-ink-2 ring-transparent hover:bg-white/60'}`}
              >
                {c.label} <b>{c.n}</b>
              </button>
            ))}
            <span className="ml-auto text-ink-2">
              <b>{t.changes ?? 0}</b> changes in <b>{t.sections_compared ?? 0}</b> compared sections
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {rows.length === 0 && (
              <div className="p-6 text-[13px] text-slate-500">No sections in this filter.</div>
            )}
            {rows.map((r) => {
              const isOpen = open === r.section_key;
              const sp = spansBySection.get(r.section_key) ?? { old: [], new: [] };
              return (
                <div key={r.section_key} className="border-b border-line">
                  <button
                    onClick={() => setOpen(isOpen ? null : r.section_key)}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50"
                  >
                    <span className="text-[13px] font-medium text-ink">§{r.section_key}</span>
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink-2">{r.title}</span>
                    {r.verdict === 'modified' && (
                      <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] text-indigo-700 ring-1 ring-indigo-200">
                        +{r.words_added} / −{r.words_removed}
                      </span>
                    )}
                    {r.verdict === 'not_compared' && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600 ring-1 ring-slate-200">
                        {r.match_status.startsWith('only_in') ? 'one source' : r.match_status}
                      </span>
                    )}
                    {r.verdict === 'unchanged' && (
                      <span className="text-[11px] text-slate-400">unchanged</span>
                    )}
                    {!r.confident && r.verdict === 'unchanged' && (
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700 ring-1 ring-amber-200">
                        partial
                      </span>
                    )}
                    <span className="text-slate-400">{isOpen ? '▾' : '▸'}</span>
                  </button>
                  {isOpen && (
                    <div className="flex gap-4 bg-slate-50/60 px-4 pb-4">
                      <Pane side={data.old} row={r} spans={sp.old} tone="old" />
                      <Pane side={data.new} row={r} spans={sp.new} tone="new" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
